import { useGetCurrentUser, useGetTenant } from '@sunstone/api-client/hooks';
import { setUserTenantType } from '@sunstone/store';
import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/store';
import { useAuthToken } from './useAuthToken';

// Vite replaces this at build time — false in all non-test builds.
const SKIP_AUTH = import.meta.env.VITE_SKIP_AUTH === 'true';

/**
 * Resolves the authenticated user's tenant type and stores it in Redux.
 * Pure side-effect — no return value.
 *
 * Gated on the DB-authoritative identity (GetCurrentUser), not the Auth0
 * JWT's roles/tenant claims — a stale claim must never drive which tenant
 * gets fetched. Tenant-less and unprovisioned identities (no primary_role_id
 * or no tenant_id) are set to 'none' immediately — checked directly off
 * currentUser.tenantId here rather than useAuthorization's isPlatformAdmin,
 * since that hook's own scope resolution depends on userTenantType, and
 * routing it back through here would be circular.
 */
export function useTenantTypeSync(): void {
  const dispatch = useAppDispatch();
  const { token } = useAuthToken();
  const { data: currentUserData } = useGetCurrentUser(token);
  const currentUser = currentUserData?.user;
  const currentTenantType = useAppSelector((state) => state.auth.userTenantType);

  const shouldFetchTenant = !SKIP_AUTH && !!currentUser?.primaryRoleId && !!currentUser.tenantId;
  const resolvedTenantId = shouldFetchTenant ? (currentUser?.tenantId ?? '') : '';
  const { data, isLoading: isFetching } = useGetTenant(resolvedTenantId, token);

  // Single effect: set 'pending' while in-flight, resolve to the correct type once settled.
  // Keeping it in one effect avoids the feedback loop that two separate effects create
  // (where resolving back to 'none' would re-arm the 'pending' transition).
  useEffect(() => {
    if (SKIP_AUTH) return;

    if (!resolvedTenantId) {
      dispatch(setUserTenantType('none'));
      return;
    }

    if (isFetching) {
      // Only move to 'pending' if we haven't already started or resolved —
      // prevents re-arming after a completed fetch.
      if (currentTenantType === 'none') {
        dispatch(setUserTenantType('pending'));
      }
      return;
    }

    // Fetch has settled (success or error). Don't re-dispatch if already resolved
    // to avoid thrashing, but always resolve out of 'pending' or 'none'.
    if (currentTenantType === 'child' || currentTenantType === 'partner') return;
    let tenantType: 'partner' | 'child' | 'none' = 'none';
    if (data?.tenant) {
      tenantType = data.tenant.parentId ? 'child' : 'partner';
    }
    dispatch(setUserTenantType(tenantType));
  }, [resolvedTenantId, isFetching, data, currentTenantType, dispatch]);
}
